/**
 * Click outside dropdown helper functions
 * Handles closing dropdowns when clicking outside of them
 */

/**
 * Creates a click outside dropdown handler
 * @param {Object} dependencies - Object containing all necessary dependencies
 */
export const createClickOutsideDropdownHandler = ({
  // Refs
  showingDropdownRef,
  autoFillDropdownRef,
  projectStatusDropdownRef,
  projectDropdownRef,
  tagDropdownRef,
  userDropdownRef,
  userStatusDropdownRef,
  timePickerRefs,
  startInputRef,
  endInputRef,
  start12HrTimeInputRef,
  end12HrTimeInputRef,
  // Setters
  setShowingDropdownOpen,
  setDropdownVisible,
  setIsProjectStatusDropdownOpen,
  setProjectDropdownOpen,
  setTagDropdownOpen,
  setIsUserDropdownOpen,
  setIsUserStatusDropdownOpen,
  setTimePickerOpen,
}) => {
  /**
   * Handle click outside dropdown to close dropdowns
   * @param {Event} event - Click event
   */
  const handleClickOutsideDropdown = (event) => {
    try {
      const isClickInsideShowingDropdown =
        showingDropdownRef.current && showingDropdownRef.current.contains(event.target);
      const isClickInsideAutoFillDropdown =
        autoFillDropdownRef.current && autoFillDropdownRef.current.contains(event.target);
      const isClickInsideProjectStatusDropdown =
        projectStatusDropdownRef.current && projectStatusDropdownRef.current.contains(event.target);
      const isClickInsideProjectDropdown =
        projectDropdownRef.current && projectDropdownRef.current.contains(event.target);
      const isClickInsideTagDropdown =
        tagDropdownRef.current && tagDropdownRef.current.contains(event.target);
      const isClickInsideUserDropdown =
        userDropdownRef.current && userDropdownRef.current.contains(event.target);
      const isClickInsideUserStatusDropdown =
        userStatusDropdownRef.current && userStatusDropdownRef.current.contains(event.target);
      const isClickInsideTimePickerStart24 =
        timePickerRefs.start24.current && timePickerRefs.start24.current.contains(event.target);
      const isClickInsideTimePickerEnd24 =
        timePickerRefs.end24.current && timePickerRefs.end24.current.contains(event.target);
      const isClickInsideTimePickerStart12 =
        timePickerRefs.start12.current && timePickerRefs.start12.current.contains(event.target);
      const isClickInsideTimePickerEnd12 =
        timePickerRefs.end12.current && timePickerRefs.end12.current.contains(event.target);

      // Check if click is on any time input
      const isClickOnTimeInput =
        startInputRef.current?.contains(event.target) ||
        endInputRef.current?.contains(event.target) ||
        start12HrTimeInputRef.current?.contains(event.target) ||
        end12HrTimeInputRef.current?.contains(event.target);

      if (!isClickInsideShowingDropdown) {
        setShowingDropdownOpen(false);
      }
      if (!isClickInsideAutoFillDropdown) {
        setDropdownVisible(false);
      }
      if (!isClickInsideProjectStatusDropdown) {
        setIsProjectStatusDropdownOpen(false);
      }
      if (!isClickInsideProjectDropdown) {
        setProjectDropdownOpen(false);
      }
      if (!isClickInsideTagDropdown) {
        setTagDropdownOpen(false);
      }
      if (!isClickInsideUserDropdown) {
        setIsUserDropdownOpen(false);
      }
      if (!isClickInsideUserStatusDropdown) {
        setIsUserStatusDropdownOpen(false);
      }

      // Only close time picker if click is outside both the picker and the input
      // Check all time pickers to see if click is inside any of them
      const isClickInsideAnyTimePicker =
        isClickInsideTimePickerStart24 ||
        isClickInsideTimePickerEnd24 ||
        isClickInsideTimePickerStart12 ||
        isClickInsideTimePickerEnd12;

      // Only close if click is outside the specific picker AND outside all pickers AND not on the input
      if (!isClickInsideTimePickerStart24 && !isClickOnTimeInput && !isClickInsideAnyTimePicker) {
        setTimePickerOpen(prev => ({ ...prev, start24: false }));
      } else if (isClickInsideTimePickerStart24) {
        // Explicitly keep it open if click is inside
        // Don't do anything - keep dropdown open
      }

      if (!isClickInsideTimePickerEnd24 && !isClickOnTimeInput && !isClickInsideAnyTimePicker) {
        setTimePickerOpen(prev => ({ ...prev, end24: false }));
      } else if (isClickInsideTimePickerEnd24) {
        // Explicitly keep it open if click is inside
        // Don't do anything - keep dropdown open
      }

      if (!isClickInsideTimePickerStart12 && !isClickOnTimeInput && !isClickInsideAnyTimePicker) {
        setTimePickerOpen(prev => ({ ...prev, start12: false }));
      } else if (isClickInsideTimePickerStart12) {
        // Explicitly keep it open if click is inside
        // Don't do anything - keep dropdown open
      }

      if (!isClickInsideTimePickerEnd12 && !isClickOnTimeInput && !isClickInsideAnyTimePicker) {
        setTimePickerOpen(prev => ({ ...prev, end12: false }));
      } else if (isClickInsideTimePickerEnd12) {
        // Explicitly keep it open if click is inside
        // Don't do anything - keep dropdown open
      }
    } catch (error) {
      console.error("Error in handleClickOutsideDropdown:", error);
    }
  };

  /**
   * Check if click target is inside a time picker dropdown
   * @param {HTMLElement} target - Click target element
   * @param {Object} timePickerRefs - Object containing time picker refs
   * @returns {boolean} True if target is inside a time picker
   */
  const checkIfInsideTimePicker = (target, timePickerRefs) => {
    if (!target) return false;

    // Check if target itself is inside any time picker
    const isInsideStart24 = timePickerRefs.start24.current?.contains(target);
    const isInsideEnd24 = timePickerRefs.end24.current?.contains(target);
    const isInsideStart12 = timePickerRefs.start12.current?.contains(target);
    const isInsideEnd12 = timePickerRefs.end12.current?.contains(target);

    if (isInsideStart24 || isInsideEnd24 || isInsideStart12 || isInsideEnd12) {
      return true;
    }

    // Also check if target has the time-picker-dropdown class or is inside an element with it
    let element = target;
    for (let i = 0; i < 10 && element; i++) { // Check up to 10 levels up
      if (element.classList?.contains('time-picker-dropdown')) {
        return true;
      }
      element = element.parentElement;
    }

    return false;
  };

  return {
    handleClickOutsideDropdown,
    checkIfInsideTimePicker,
  };
};
